import React, { useState } from "react";
import classes from "./addUser.module.scss";
import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Divider from "@mui/material/Divider";
import {
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useCreateUserMutation } from "../../../api/authApi";
import toast from "react-hot-toast";
import { useGetRolesQuery } from "../../../api/roleApi";
import { IRole } from "../../../interfaces/Itable";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { User } from "../../userControlComp/UserControlComp";

interface Employees {
  emp_id?: string;
  full_name?: string;
  email?: string;
  role_id?: number;
}

interface AddUserProps {
  employees: Employees[];
  onClose: () => void;
}

interface NewUser {
  emp_id?: string;
  username?: string;
  email?: string;
  password?: string;
  role_id?: number;
}

const AddUser: React.FC<AddUserProps> = ({ employees, onClose }) => {
  const userInfo = useSelector((state: RootState) => state.auth.user as User | null);

  const [createUser] = useCreateUserMutation();
  const [user, setNewUser] = useState<NewUser>({});
  const [showPassword, setShowPassword] = useState(false);
  const { data: roleOptions } = useGetRolesQuery();

  // ✅ Password validation
  const validatePassword = (password?: string) => {
    if (!password) return false;
    const lengthOk = password.length >= 8;
    const upperOk = /[A-Z]/.test(password);
    const numberOk = /[0-9]/.test(password);
    const specialOk = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return lengthOk && upperOk && numberOk && specialOk;
  };

  // ✅ Form validation
  const isFormValid = () => {
    return (
      !!user.emp_id &&
      !!user.email &&
      !!user.role_id &&
      validatePassword(user.password)
    );
  };

  const handleCreateUser = async () => {
    if (!user?.email || !user?.role_id || !user?.emp_id) {
      toast.error("Please select a user, role, and provide password if required");
      return;
    }
    try {
      const result = await createUser({
        email: user.email,
        role_id: user.role_id,
        username: user.username,
        emp_id: user.emp_id,
        password: user.password,
      }).unwrap();

      if (result.success) {
        toast.success(`User created. Password: ${result.user?.password}`, {
          duration: 8000,
          style: { fontFamily: "Poppins", fontSize: "14px" },
        });
        onClose();
      } else {
        toast.error(result.message || "Something went wrong!");
      }
    } catch (err: any) {
      console.error("Error creating user:", err);
      toast.error(err?.data?.message || "Failed to create user");
    }
  };

  return (
    <div className={classes.addUser}>
      <div className={classes.addUser__heading}>
        <h3>Add User</h3>
        <Close sx={{ color: "red", cursor: "pointer" }} onClick={onClose} />
      </div>

      <Divider sx={{ width: "100%" }} />

      <div className={classes.addUser__formGrid}>
        {/* Employee Selector */}
        <Autocomplete<Employees>
          options={employees}
          getOptionLabel={(option) => option.full_name || ""}
          onChange={(_, newValue) => {
            if (newValue) {
              setNewUser((prev) => ({
                ...prev,
                emp_id: newValue.emp_id,
                username: newValue.full_name,
                email: newValue.email,
              }));
            } else {
              setNewUser({});
            }
          }}
          value={
            user.username
              ? employees.find((u) => u.full_name === user.username) || null
              : null
          }
          renderInput={(params) => (
            <TextField {...params} label="Employee Name" size="small" />
          )}
          slotProps={{
            paper: {
              sx: {
                fontFamily: "Poppins, sans-serif",
                "& .MuiAutocomplete-option": {
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                },
              },
            },
          }}
        />

        {/* Employee ID */}
        <TextField label="Emp ID" value={user.emp_id || ""} size="small" disabled />

        {/* Email */}
        <TextField label="E-mail ID" value={user.email || ""} size="small" disabled />

        {/* Password */}
        <TextField
          label="Set Password"
          type={showPassword ? "text" : "password"}
          value={user.password || ""}
          size="small"
          onChange={(e) =>
            setNewUser((prev) => ({ ...prev, password: e.target.value }))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Role */}
        <Autocomplete<IRole>
          options={
            roleOptions
              ? roleOptions.filter((role) =>
                userInfo?.role_type === "Admin" ? true : role.role_type !== "Admin"
              )
              : []
          }
          getOptionLabel={(option) => option.role_type}
          onChange={(_, newValue) =>
            setNewUser((prev) => ({ ...prev, role_id: newValue?.role_id }))
          }
          value={
            user.role_id
              ? roleOptions?.find((opt) => opt.role_id === user.role_id) || null
              : null
          }
          renderInput={(params) => (
            <TextField {...params} label="Role" size="small" />
          )}
          slotProps={{
            paper: {
              sx: {
                fontFamily: "Poppins, sans-serif",
                "& .MuiAutocomplete-option": {
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                },
              },
            },
          }}
        />

      </div>

      {/* Password Rules */}
      <div>
        {user.password && (
          <div style={{ marginTop: "6px", fontSize: "12px", fontFamily: "Poppins" }}>
            <div style={{ color: user.password.length >= 8 ? "green" : "red" }}>
              • At least 8 characters
            </div>
            <div style={{ color: /[A-Z]/.test(user.password) ? "green" : "red" }}>
              • At least one uppercase letter
            </div>
            <div style={{ color: /[0-9]/.test(user.password) ? "green" : "red" }}>
              • At least one number
            </div>
            <div
              style={{
                color: /[!@#$%^&*(),.?":{}|<>]/.test(user.password) ? "green" : "red",
              }}
            >
              • At least one special character
            </div>
          </div>
        )}
      </div>

      <Divider sx={{ width: "100%" }} />

      {/* Submit Button */}
      <div className={classes.addUser__buttonBox}>
        <Button
          type="submit"
          variant="contained"
          onClick={handleCreateUser}
          startIcon={<PersonAddIcon style={{ color: "#fff", fontSize: "14px" }} />}
          disabled={!isFormValid()}   // ✅ disabled until form is valid
        >
          Add User
        </Button>
      </div>
    </div>
  );
};

export default AddUser;
